import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Grid,
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import SecurityIcon from '@mui/icons-material/Security';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import TerminalIcon from '@mui/icons-material/Terminal';
import PhoneIcon from '@mui/icons-material/Phone';
import MicIcon from '@mui/icons-material/Mic';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CreateIcon from '@mui/icons-material/Create';
import VoiceChatIcon from '@mui/icons-material/VoiceChat';
import TranslateIcon from '@mui/icons-material/Translate';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReplayIcon from '@mui/icons-material/Replay';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import BuildIcon from '@mui/icons-material/Build';

function MembershipPopup() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        color: '#fff',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          color: 'text.primary',
          p: 4,
          width: '70%',
        }}
      >
        {/* Header with H1 and Upgrade Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
            Purchase Beta Access
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<UpgradeIcon />}
              onClick={() => navigate('/billing')}
              sx={{ textTransform: 'none', fontWeight: 'bold', px: 3, py: 1, borderRadius: '8px' }}
            >
              View Plans
            </Button>
            <Button
              component="a"
              href="https://calendly.com/alex-stealthnet/30min"
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={{ textTransform: 'none', fontWeight: 'bold', px: 3, py: 1, borderRadius: '8px' }}
            >
              Schedule Sales Call
            </Button>
          </Box>
        </Box>

        {/* Hero Image */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
            <iframe
              title="Arcade Demo"
              src="https://demo.arcade.software/fd5WynPHqtQM6S8wKMXo?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: '0',
              }}
              allow="autoplay; fullscreen"
            />
          </div>
        </Box>

        {/* Title and Beta Tag */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 'bold', mr: 1 }}>
            AI Agent - Automated Penetration Testing
          </Typography>
          <Typography
            variant="body2"
            sx={{
              backgroundColor: 'warning.light',
              color: 'warning.contrastText',
              px: 1,
              py: 0.5,
              borderRadius: '4px',
              fontWeight: 'bold',
            }}
          >
            BETA
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{ color: 'text.secondary', mb: 3, textAlign: 'center' }}
        >
          Supercharge your pentesting with an AI-powered agent that automates
          reconnaissance, finds vulnerabilities, generates exploit POCs, and
          drafts comprehensive reports.
        </Typography>

        {/* Pentest Types Chips */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            mb: 3,
          }}
        >
          {[
            { label: 'External', available: true },
            { label: 'Vishing', available: true },
            { label: 'Dark Web Monitoring', available: true },
            { label: 'Internal', available: false },
            { label: 'Phishing', available: false },
            { label: 'Source Code Review', available: false },
            { label: 'Web Application', available: false },
          ].map((type) => (
            <Box key={type.label} sx={{ m: 0.5 }}>
              <Chip
                label={type.label}
                color={type.available ? 'primary' : 'default'}
                variant={type.available ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: type.available ? 'bold' : 'normal',
                  opacity: type.available ? 1 : 0.5,
                }}
              />
            </Box>
          ))}
        </Box>
        <Typography
          variant="body2"
          sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}
        >
          Currently supporting <strong>External Pentesting</strong>, <strong>Vishing Calls</strong>, and <strong>Dark Web Monitoring</strong> only. More to come!
        </Typography>

        {/* AI Agent Cards */}
        <Grid container spacing={3} mb={3}>
          {/* External Agent Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardHeader
                title="External Agent"
                action={
                  <Button
                    component="a"
                    href="https://drive.google.com/file/d/1gbaVd5JvpkTiVojhgKiW2b8W4q8yMSk2/view?usp=drive_link"
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="outlined"
                  >
                    Sample Report
                  </Button>
                }
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <List>
                  <ListItem>
                    <ListItemIcon><SearchIcon /></ListItemIcon>
                    <ListItemText primary="Automates Recon, Fingerprint, and Exploitation phases" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><PublicIcon /></ListItemIcon>
                    <ListItemText primary="Works with domains, IPs, and CIDRs" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><SubdirectoryArrowRightIcon /></ListItemIcon>
                    <ListItemText primary="Automatically finds subdomains and shows IT" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><SecurityIcon /></ListItemIcon>
                    <ListItemText primary="Performs CVE scan" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><ListAltIcon /></ListItemIcon>
                    <ListItemText primary="Checks OWASP Top 10" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><DescriptionIcon /></ListItemIcon>
                    <ListItemText primary="Automatically writes pentest, executive, CVE reports & letter of attestation" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><TerminalIcon /></ListItemIcon>
                    <ListItemText primary="Leverages an AI agent on Kali Linux" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Vishing Agent Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardHeader
                title="Vishing Agent"
                action={
                  <Button
                    component="a"
                    href="https://vimeo.com/1064351192"
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="outlined"
                  >
                    Example Call
                  </Button>
                }
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <List>
                  <ListItem>
                    <ListItemIcon><PhoneIcon /></ListItemIcon>
                    <ListItemText primary="World’s first AI vishing call automation" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><MicIcon /></ListItemIcon>
                    <ListItemText primary="Leverages hyper-realistic AI voices" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><LibraryBooksIcon /></ListItemIcon>
                    <ListItemText primary="Library of vishing scenarios" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CreateIcon /></ListItemIcon>
                    <ListItemText primary="Create your own custom scenario" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><VoiceChatIcon /></ListItemIcon>
                    <ListItemText primary="Multiple voices to pick from" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><TranslateIcon /></ListItemIcon>
                    <ListItemText primary="Multi-language support (Spanish, Russian, Mandarin, etc.)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><ContentCopyIcon /></ListItemIcon>
                    <ListItemText primary="Voice Cloning" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><ReplayIcon /></ListItemIcon>
                    <ListItemText primary="Record & replay phone calls" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><DescriptionIcon /></ListItemIcon>
                    <ListItemText primary="Detailed vishing report" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Dark Web Monitoring Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardHeader title="Dark Web Monitoring" />
              <CardContent sx={{ flexGrow: 1 }}>
                <List>
                  <ListItem>
                    <ListItemIcon><LockOpenIcon /></ListItemIcon>
                    <ListItemText primary="Discover exposed credentials" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><DescriptionIcon /></ListItemIcon>
                    <ListItemText primary="Full report" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><BuildIcon /></ListItemIcon>
                    <ListItemText primary="Credential testing engine" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Call to Action */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Button
            component="a"
            href="https://calendly.com/alex-stealthnet/30min"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            color="primary"
            size="large"
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              borderRadius: '8px',
              boxShadow: (theme) => theme.shadows[4],
            }}
          >
            Upgrade to AI Pentest Pro
          </Button>
          <Typography
            variant="body2"
            sx={{ mt: 2, color: 'text.secondary' }}
          >
            30-Day Money-Back Guarantee
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default MembershipPopup;
